import React, { useEffect, useMemo } from "react";
import { useMonth } from "@datepicker-react/hooks";
import { format, parseISO } from "date-fns";
import Day from "../Day";
import {
  useCalendarData,
  calendarViews,
  getIsNextMonthsDisabled,
  getIsPreviousMonthsDisabled,
} from "../../utils";
import CalendarIcon from "../../../../svg/CalendarIcon/CalendarIcon";
import { Chevron } from "../../../../svg";
import { chevronDirection } from "../../../../svg/Svg";
import { useReactAppState } from "../../../../../shared/containers/utils";
import { getCultureLocalizer } from "../../../../utils";

function Month({
  year,
  month,
  firstDayOfWeek,
  goToPreviousMonths,
  goToNextMonths,
  mlsCalendarMatchesData,
}) {
  const { days, weekdayLabels, monthLabel } = useMonth({
    year,
    month,
    firstDayOfWeek,
    weekdayLabelFormat: (date) =>
      format(date, "ccccc", { locale: getCultureLocalizer() }),
    dayLabelFormat: (date) => format(date, "d"),
    monthLabelFormat: (date) =>
      format(date, "MMMM YYY", { locale: getCultureLocalizer() }),
  });
  const { t } = useReactAppState();
  const {
    setView,
    onTodaySelect,
    setCurrentYear,
    currentYear,
    activeMonths,
    minDate,
    maxDate,
  } = useCalendarData();

  useEffect(() => {
    if (year !== currentYear) {
      setCurrentYear(year);
    }
  }, [year]);

  const { isNextMonthDisabled, isPreviousMonthDisabled } = useMemo(() => {
    return {
      isNextMonthDisabled: getIsNextMonthsDisabled({
        activeMonths,
        minDate,
        maxDate,
      }),
      isPreviousMonthDisabled: getIsPreviousMonthsDisabled({
        activeMonths,
        minDate,
        maxDate,
      }),
    };
  }, [activeMonths, minDate, maxDate]);

  return (
    <div className="mls-o-calendar__month-view">
      <div className="mls-o-calendar__header">
        <button
          type="button"
          className="mls-o-calendar__prev-month"
          disabled={isPreviousMonthDisabled}
          onClick={isPreviousMonthDisabled ? null : goToPreviousMonths}
        >
          <Chevron direction={chevronDirection.left} />
        </button>
        <button
          type="button"
          onClick={() => setView(calendarViews.months)}
          className="mls-o-calendar__title"
        >
          <CalendarIcon />
          <span className="mls-o-calendar__title-label">{monthLabel}</span>
        </button>
        <button
          type="button"
          className="mls-o-calendar__next-month"
          disabled={isNextMonthDisabled}
          onClick={isNextMonthDisabled ? null : goToNextMonths}
        >
          <Chevron direction={chevronDirection.right} />
        </button>
      </div>
      <div className="mls-o-calendar__day-labels">
        {weekdayLabels.map((dayLabel, index) => {
          return (
            <div
              className="mls-o-calendar__weekday-label"
              key={`${dayLabel}-${index}`}
            >
              {dayLabel}
            </div>
          );
        })}
      </div>
      <div className="mls-o-calendar__day-grid">
        {days.map((day, index) => {
          if (!day?.date) {
            return (
              <div
                key={`$empty-day-${index}`}
                aria-hidden="true" 
              />
            );
          }

          const matchDay = format(day?.date, "yyyy-MM-dd");
          const isMatchDay = mlsCalendarMatchesData?.get(matchDay);

          return (
            <Day
              date={day.date}
              key={`${day.dayLabel}-${index}`}
              day={day.dayLabel}
              isMatchDay={isMatchDay}
            />
          );
        })}
      </div>
      <button onClick={onTodaySelect} className="mls-o-calendar__today">
        {t("today")}
      </button>
    </div>
  );
}

export default Month;
